import { computed, ref, watch, onBeforeUnmount, toValue, type Ref } from 'vue';
import { ibWS } from '@ibgroup-tws-webapps/ib.utils';
import { useUserState } from '@/stores/useUserState';

interface RaceOiEntry {
  productConid: number;
  exchange: string;
}

interface UseRaceOiStreamOptions {
  /** Product conid + exchange for each race, derived from visible races */
  visibleRaceEntries: Ref<RaceOiEntry[]>;
}

interface ActiveSub {
  requestId: string;
  subscription: { unsubscribe(): void };
}

async function makeRequestId(suffix: string): Promise<string> {
  const webAppId = await ibWS.getWebAppId();
  return `race-oi-${suffix}-${webAppId}`;
}

/**
 * Streams product-level (race) OI via WebSocket for visible races.
 * Auth: subscribes to /event-contract/open-interest per visible race productConid.
 * Non-auth: returns empty map (caller falls through to REST query or seed).
 */
export function useRaceOiStream({ visibleRaceEntries }: UseRaceOiStreamOptions) {
  const userState = useUserState();
  const shouldStream = computed(() => !toValue(userState.usePublicApi));

  const raceOiMap = ref<Map<string, number>>(new Map());
  const activeSubs = new Map<string, ActiveSub>();

  async function subscribe(entry: RaceOiEntry) {
    const key = `race-oi-${entry.productConid}`;
    if (activeSubs.has(key)) return;

    const requestId = await makeRequestId(String(entry.productConid));
    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: { destination: '/event-contract/open-interest', 'x-target': 'mdfarm', id: requestId },
        body: { conid: entry.productConid, exchange: entry.exchange },
      },
    });

    const observable = ibWS.receive(requestId);
    if (!observable) return;

    const subscription = observable.subscribe({
      /* eslint-disable @typescript-eslint/no-explicit-any */
      next: (msg: any) => {
        const body = msg?.payload ?? msg?.body ?? msg;
        if (!body) return;
        const put = typeof body.putInterest !== 'undefined' ? Number(body.putInterest) : 0;
        const call = typeof body.callInterest !== 'undefined' ? Number(body.callInterest) : 0;
        const total = put + call;
        const current = raceOiMap.value.get(String(entry.productConid));
        if (current !== total) {
          raceOiMap.value.set(String(entry.productConid), total);
          raceOiMap.value = new Map(raceOiMap.value);
        }
      },
    });

    activeSubs.set(key, { requestId, subscription });
  }

  function unsubscribeByKey(key: string) {
    const sub = activeSubs.get(key);
    if (!sub) return;
    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: { destination: '/md/stream/unsubscribe', 'x-target': 'mdfarm', id: sub.requestId },
      },
    });
    sub.subscription.unsubscribe();
    activeSubs.delete(key);
  }

  function unsubscribeAll() {
    for (const key of Array.from(activeSubs.keys())) {
      unsubscribeByKey(key);
    }
  }

  const entriesKey = computed(() =>
    visibleRaceEntries.value
      .map((e) => e.productConid)
      .sort((a, b) => a - b)
      .join(','),
  );

  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  function reconcile() {
    if (!shouldStream.value) return;

    const neededKeys = new Set<string>();
    for (const entry of visibleRaceEntries.value) {
      const key = `race-oi-${entry.productConid}`;
      neededKeys.add(key);
      subscribe(entry);
    }

    for (const key of Array.from(activeSubs.keys())) {
      if (!neededKeys.has(key)) {
        unsubscribeByKey(key);
      }
    }
  }

  watch(
    entriesKey,
    (key) => {
      clearTimeout(debounceTimer);
      if (!shouldStream.value || !key) {
        unsubscribeAll();
        return;
      }
      debounceTimer = setTimeout(reconcile, 150);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    clearTimeout(debounceTimer);
    unsubscribeAll();
  });

  return { raceOiMap };
}
